import jsPDF from 'jspdf';
import type { Order } from '../services/orderService';

export function generateOrderReceiptPDF(
  order: Order, 
  userInfo?: { name?: string; phone?: string; email?: string }, 
  companyInfo?: { name?: string; email?: string; phone?: string }
): jsPDF {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  // Helper functions
  const formatDate = (date: Date | string) => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }) + ' ' + dateObj.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // PDF Settings
  const pageWidth = 210; // A4 width in mm
  const pageHeight = 297; // A4 height in mm
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  const footerHeight = 20;
  const maxY = pageHeight - margin - footerHeight; // Leave space for footer
  let y = margin;
  let currentPage = 1;
  
  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace: number = 10) => {
    if (y + requiredSpace > maxY) {
      // Add footer to current page
      addFooter();
      
      // Add new page
      pdf.addPage();
      currentPage++;
      y = margin;
      
      // Add header to new page
      addHeader();
      y = 40;
      
      return true;
    }
    return false;
  };
  
  // Function to add header
  const addHeader = () => {
    pdf.setFillColor(31, 41, 55); // Dark gray
    pdf.rect(0, 0, pageWidth, 30, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(companyInfo?.name || 'Demo Crackers', pageWidth / 2, 12, { align: 'center' });
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Enquiry Receipt', pageWidth / 2, 20, { align: 'center' });
  };
  
  // Function to add footer
  const addFooter = () => {
    const footerY = pageHeight - footerHeight;
    pdf.setFillColor(249, 250, 251);
    pdf.rect(0, footerY, pageWidth, footerHeight, 'F');
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(31, 41, 55);
    pdf.text('Thank you for your enquiry!', pageWidth / 2, footerY + 6, { align: 'center' });
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(107, 114, 128);
    const footerText = `${companyInfo?.name || 'Demo Crackers'} - Quality Fireworks • ${companyInfo?.email || 'democrackers@gmail.com'}`;
    pdf.text(footerText, pageWidth / 2, footerY + 11, { align: 'center' });
    
    // Add page number
    pdf.setFontSize(8);
    pdf.text(`Page ${currentPage}`, pageWidth - margin, footerY + 15, { align: 'right' });
  };

  // Add initial header
  addHeader();
  y = 40;

  // Enquiry Details Section
  pdf.setTextColor(31, 41, 55);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Enquiry Details', margin, y);
  y += 2;
  
  // Draw line
  pdf.setDrawColor(229, 231, 235);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 8;

  // Enquiry info in grid
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  
  // Row 1
  const col1X = margin;
  const col2X = margin + 55;
  const col3X = margin + 110;
  const col4X = margin + 140;
  
  // Labels
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(107, 114, 128);
  pdf.text('ENQUIRY#:', col1X, y);
  pdf.text('DATE:', col2X, y);
  pdf.text('STATUS:', col3X, y);
  pdf.text('PAYMENT:', col4X, y);
  
  y += 5;
  
  // Values
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(31, 41, 55);
  pdf.text(order.orderNumber || order.id, col1X, y);
  pdf.text(formatDate(order.createdAt), col2X, y);
  
  // Status badges
  const statusText = formatStatus(order.status);
  const statusWidth = pdf.getTextWidth(statusText) + 4;
  pdf.setFillColor(245, 158, 11); // Orange for pending
  pdf.rect(col3X - 2, y - 4, statusWidth, 6, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.text(statusText, col3X, y);
  
  const paymentText = formatStatus(order.paymentStatus);
  const paymentWidth = pdf.getTextWidth(paymentText) + 4;
  pdf.setFillColor(245, 158, 11); // Orange for pending
  pdf.rect(col4X - 2, y - 4, paymentWidth, 6, 'F');
  pdf.text(paymentText, col4X, y);
  
  y += 10;
  
  // Row 2
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(107, 114, 128);
  pdf.text('CUSTOMER:', col1X, y);
  pdf.text('PHONE:', col2X, y);
  
  
  y += 5;
  
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(31, 41, 55);
  pdf.text(userInfo?.name || 'N/A', col1X, y);
  pdf.text(userInfo?.phone || 'N/A', col2X, y);
  
  
  y += 10;
  
  // Delivery Address
  if (order.shippingAddress) {
    checkNewPage(15); // Check if we have space for address
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(107, 114, 128);
    pdf.text('DELIVERY ADDRESS:', col1X, y);
    y += 5;
    
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(31, 41, 55);
    const address = `${order.shippingAddress.street}, ${order.shippingAddress.city}, ${order.shippingAddress.state} - ${order.shippingAddress.zipCode}`;
    const addressLines = pdf.splitTextToSize(address, contentWidth);
    checkNewPage(addressLines.length * 5 + 5); // Check if address fits
    pdf.text(addressLines, col1X, y);
    y += addressLines.length * 5 + 5;
  }

  // Items Section
  y += 5;
  checkNewPage(30); // Check space for items header
  
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Items', margin, y);
  y += 2;
  
  pdf.setDrawColor(229, 231, 235);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 8;

  // Function to draw table header
  const drawTableHeader = () => {
    pdf.setFillColor(249, 250, 251);
    pdf.rect(margin, y - 6, contentWidth, 8, 'F');
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(107, 114, 128);
    
    const itemColX = margin + 2;
    const qtyColX = margin + 100;
    const priceColX = margin + 125;
    const totalColX = margin + 155;
    
    pdf.text('ITEM', itemColX, y);
    pdf.text('QTY', qtyColX, y);
    pdf.text('PRICE', priceColX, y);
    pdf.text('TOTAL', totalColX, y);
    
    y += 8;
  };
  
  // Draw initial table header
  drawTableHeader();
  
  const itemColX = margin + 2;
  const qtyColX = margin + 100;
  const priceColX = margin + 125;
  const totalColX = margin + 155;

  // Table Rows
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(31, 41, 55);
  pdf.setFontSize(10);
  
  order.items.forEach((item, index) => {
    // Calculate space needed for this item
    const itemText = item.productName + (item.sku ? ` (${item.sku})` : '');
    const itemLines = pdf.splitTextToSize(itemText, 95);
    const itemHeight = Math.max(7, itemLines.length * 5 + 2);
    
    // Check if we need a new page
    if (checkNewPage(itemHeight + 10)) {
      // Redraw table header on new page
      drawTableHeader();
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(10);
    }
    
    // Alternate row colors
    if (index % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, y - 5, contentWidth, itemHeight, 'F');
    }
    
    // Item name and SKU
    pdf.text(itemLines, itemColX, y);
    
    // Quantity, Price, Total
    pdf.text(item.quantity.toString(), qtyColX, y);
    // Format price ensuring clean number input
    const formatPrice = (price: number | string) => {
      // Convert to number and remove any existing formatting
      const cleanNumber = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.-]/g, '')) : price;
      const num = Math.round(cleanNumber || 0);
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };
    
    pdf.text('Rs. ' + formatPrice(item.price || 0), priceColX, y);
    pdf.text('Rs. ' + formatPrice(item.total || 0), totalColX, y);
    
    y += itemHeight;
  });

  // Summary
  y += 5;
  
  // Check if we have enough space for the summary (estimate 60mm needed)
  checkNewPage(60);
  
  pdf.setDrawColor(229, 231, 235);
  pdf.line(margin + 100, y, pageWidth - margin, y);
  y += 8;

  // Summary rows
  pdf.setFontSize(10);
  const summaryLabelX = margin + 110;
  const summaryValueX = totalColX;
  
  // Helper function for formatting currency
  const formatCurrency = (amount: number | string): string => {
    // Convert to number and remove any existing formatting
    const cleanNumber = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]/g, '')) : amount;
    const num = Math.round(cleanNumber || 0);
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Subtotal
  pdf.text('Subtotal:', summaryLabelX, y);
  pdf.text('Rs. ' + formatCurrency(order.subtotal || 0), summaryValueX, y);
  y += 6;

  // Discount
  if (order.discount && order.discount > 0) {
    pdf.text(`Discount${order.couponCode ? ` (${order.couponCode})` : ''}:`, summaryLabelX, y);
    pdf.text('-Rs. ' + formatCurrency(order.discount), summaryValueX, y);
    y += 6;
  }

  // Tax
  if (order.tax && order.tax > 0) {
    pdf.text('Tax:', summaryLabelX, y);
    pdf.text('Rs. ' + formatCurrency(order.tax), summaryValueX, y);
    y += 6;
  }

  // Shipping
  if (order.shipping && order.shipping > 0) {
    pdf.text('Shipping:', summaryLabelX, y);
    pdf.text('Rs. ' + formatCurrency(order.shipping), summaryValueX, y);
    y += 6;
  }

  // Packaging
  if (order.packing && order.packing > 0) {
    pdf.text('Packaging:', summaryLabelX, y);
    pdf.text('Rs. ' + formatCurrency(order.packing), summaryValueX, y);
    y += 6;
  }

  // Total
  pdf.setDrawColor(229, 231, 235);
  pdf.line(margin + 100, y, pageWidth - margin, y);
  y += 6;
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('Total:', summaryLabelX, y);
  pdf.text('Rs. ' + formatCurrency(order.total || 0), summaryValueX, y);

  // Customer Notes
  if (order.notes) {
    y += 15;
    
    // Check if we have space for notes
    checkNewPage(40);
    
    pdf.setFillColor(249, 250, 251);
    pdf.rect(margin, y - 5, contentWidth / 2, 30, 'F');
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(107, 114, 128);
    pdf.text('CUSTOMER NOTES:', margin + 2, y);
    
    y += 5;
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(31, 41, 55);
    pdf.setFontSize(10);
    const noteLines = pdf.splitTextToSize(order.notes, contentWidth / 2 - 4);
    
    // Check if note lines fit
    checkNewPage(noteLines.length * 5);
    
    pdf.text(noteLines, margin + 2, y);
  }

  // Add footer to the last page
  addFooter();

  return pdf;
}